import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";

/**
 * Multi-link Phase 2: when a customer reaches a partner's verified custom
 * domain (e.g. pay.bet1.acme.com/), this component asks the backend
 * `/api/public/by-host` to map the Host header to a `link_code`, then
 * redirects the SPA to /pay/{code}. If the host is the canonical gateway
 * or unmapped, it does nothing.
 *
 * Only fires for the root path so deep links continue to work as-is.
 */
export default function HostRedirector() {
    const nav = useNavigate();
    const loc = useLocation();
    useEffect(() => {
        // Only resolve at root. Anything deeper (e.g. /admin, /pay/x) stays put.
        if (loc.pathname !== "/" && loc.pathname !== "") return;
        // Skip the canonical/preview host — backend FRONTEND_URL/REACT_APP_BACKEND_URL.
        const apiHost = (process.env.REACT_APP_BACKEND_URL || "")
            .replace(/^https?:\/\//, "").replace(/\/.*/, "").toLowerCase();
        const here = window.location.hostname.toLowerCase();
        if (!here || here === apiHost) return;
        // Skip well-known dev hosts so HMR doesn't fire spurious requests.
        if (here === "localhost" || here.endsWith(".localhost") || /^\d+\.\d+\.\d+\.\d+$/.test(here)) return;

        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/public/by-host?host=${encodeURIComponent(here)}`);
                if (cancelled) return;
                if (r.data?.link_code) nav(`/pay/${r.data.link_code}`, { replace: true });
            } catch { /* not mapped — show landing as-is */ }
        })();
        return () => { cancelled = true; };
    }, [loc.pathname, nav]);
    return null;
}
